import _ from 'underscore';

import { L10n } from '../../commonInfra/L10n/L10n';
import { ListResult } from '../../httpClient/fetchers/ListResult';
import { ITypedId } from '../../httpClient/serverApi/types/ITypedId';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ExplicitPermissionsResult } from '../requests/ExplicitPermissions';
import { GetEffectivePermissionsRequest } from '../requests/types/GetEffectivePermissionsRequest';
import { GetExplicitPermissionsProjectsRequest, GetExplicitPermissionsRequest } from '../requests/types/GetExplicitPermissionsRequest';
import { IAuthorizableGranteePermissions } from '../requests/types/IAuthorizableGranteePermissions';
import { CapabilityValue, ICapabilityValue } from '../requests/types/ICapabilityValue';
import { SetExplicitPermissionsRequest } from '../requests/types/SetExplicitPermissionsRequest';
import { AuthorizableType } from '../types/AuthorizableType';
import { Capabilities } from '../types/Capabilities';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { ITypedIds } from '../types/ITypedIds';
import {
  IPermission,
  Permission,
  PermissionReason
} from '../types/Permission';

export module PermissionsHelper {

  export function granteeName(row: IGranteePermissions): string {
    if (row.group) {
      if (row.group.userCount >= 0) {
        // TODO: It would be better if this was a localized string replacement,
        // but that would require some minor refactoring, because this static
        // method doesn't have easy access to $translate.
        return row.group.name + ' (' + row.group.userCount + ')';
      } else {
        return row.group.name;
      }
    } else if (row.user) {
      return row.user.displayName;
    } else if(row.groupSet) {
      return row.groupSet.name;
    } else {
      return '';
    }
  }

  export function compareGrantees(a: IGranteePermissions, b: IGranteePermissions): number {
    // Check if either 'a' or 'b' is the 'All Users' group
    const aIsAllUsersGroup = a.group && a.group.isAllUsersGroup;
    const bIsAllUsersGroup = b.group && b.group.isAllUsersGroup;

    // Special case: If 'a' is the 'All Users' group, it should come first
    if (aIsAllUsersGroup) {
      return -1;
    }

    // Special case: If 'b' is the 'All Users' group, it should come first
    if (bIsAllUsersGroup) {
      return 1;
    }

    const aType = getGranteeType(a);
    const bType = getGranteeType(b);

    if (aType !== bType) {
      return aType - bType; // Example: aType is groupSet (1) and bType is Group (2): 1 - 2 = -1 (groupSet comes before groups)
    }

    if (aType === GranteeType.GroupSet) {
      return compareStrings(a.groupSet.name, b.groupSet.name);
    }

    if (aType === GranteeType.Group) {
      return compareStrings(a.group.name, b.group.name);
    }

    if (aType === GranteeType.User) {
      return compareStrings(a.user.displayName, b.user.displayName);
    }

    return 0; // Default case

  }

  function getGranteeType(grantee: IGranteePermissions): GranteeType {
    if (grantee.groupSet) {
      return GranteeType.GroupSet;
    }
    if (grantee.group) {
      return GranteeType.Group;
    }
    if (grantee.user) {
      return GranteeType.User;
    }
    return GranteeType.Unknown; // Default case
  }

  export enum GranteeType { // Precedence order for explicit permissions display
    Unknown = 0,
    GroupSet = 1,
    Group = 2,
    User = 3,
  }

  function compareStrings(a: string, b: string): number {
    if(L10n.language){
      return a.localeCompare(b, L10n.language.replace('_','-'));
    }
    return a.localeCompare(b);
  }

  export function permissionFromIGranteePermission(
      igp: IGranteePermissions, contentType: string, capability: string): IPermission {
    const permissions = permissionsFromIGranteePermission(igp, contentType);
    return permissions && permissions[capability];
  }

  export function permissionsFromIGranteePermission(
      igp: IGranteePermissions, contentType: string): _.Dictionary<IPermission> {
    return igp && igp.permissionsByContentType && igp.permissionsByContentType[contentType];
  }

  export function capabilityReason(
      row: IGranteePermissions,
      contentType: string,
      capability: string,
      defaultValue: string
  ): string {
    const permission = permissionFromIGranteePermission(row, contentType, capability);
    if (permission) {
      switch (permission.reason) {
        case PermissionReason.GroupAllow:                   return 'group_allow';
        case PermissionReason.GroupDeny:                    return 'group_disallow';
        case PermissionReason.GroupProjectLeaderAllow:      return 'group_project_leader';
        case PermissionReason.GuestAllow:                   return 'guest_allowed';
        case PermissionReason.DerivedAllow:                 return 'derived_allow';
        case PermissionReason.DerivedWriteAllow:            return 'derived_allow';
        case PermissionReason.ImplicitDeny:                 return 'implicit_deny';
        case PermissionReason.Mixed:                        return 'mixed';
        case PermissionReason.Unspecified:                  return 'unspecified';
        case PermissionReason.UserAdminAllow:               return 'user_admin';
        case PermissionReason.UserAllow:                    return 'user_allow';
        case PermissionReason.UserAuthorizableOwnerAllow:   return 'user_authorizable_owner';
        case PermissionReason.UserDeny:                     return 'user_disallow';
        case PermissionReason.UserLicensingDeny:            return 'user_licensing';
        case PermissionReason.UserProjectLeaderAllow:       return 'user_project_leader';
        case PermissionReason.UserProjectOwnerAllow:        return 'user_project_owner';
        default:                                            return defaultValue;
      }
    } else {
      return defaultValue;
    }
  }

  export function capabilityValue(
      row: IGranteePermissions,
      contentType: string,
      capability: string,
      defaultValue: string
  ): string {
    const permission = permissionFromIGranteePermission(row, contentType, capability);
    if (permission) {
      switch (permission.permission) {
        case Permission.Allowed: return 'allowed';
        case Permission.Denied: return 'denied';
        case Permission.Mixed: return 'mixed';
        default: return defaultValue;
      }
    } else {
      return defaultValue;
    }
  }

  export function capabilityTitleKey(
      row: IGranteePermissions,
      contentType: string,
      capability: string,
      defaultValue: string
  ): string {
    const curr = permissionFromIGranteePermission(row, contentType, capability);
    if (curr && curr.reason !== PermissionReason.Mixed && curr.reason !== PermissionReason.Unspecified) {
      // we have a reason for this capability; it's neither mixed nor unspecified, display the reason
      return 'permission_reason_' + capabilityReason(row, contentType, capability, defaultValue);
    } else {
      // we either don't have a reason, or the reason is mixed or unspecified, display only the value
      return 'permission_value_' + capabilityValue(row, contentType, capability, defaultValue);
    }
  }

  export function pluckPermissionsFromDictionary(input: _.Dictionary<IPermission>): _.Dictionary<Permission> {
    const result: _.Dictionary<Permission> = {};
    _.each(input, (value: IPermission, key: string) => {
      result[key] = value.permission;
    });
    return result;
  }

  // returns a new dictionary of IPermissions with all reasons set to 'allowed'
  export function AllowedIPermissions(permissions: _.Dictionary<IPermission>, reason: PermissionReason): _.Dictionary<IPermission> {
    const allowedPermissions: _.Dictionary<IPermission> = {};
    _.each(permissions, (permission: IPermission, key: any) => {
      allowedPermissions[key] = {
        permission: Permission.Allowed,
        reason,
      };
    });
    return allowedPermissions;
  }

  // returns a new dictionary of IPermissions with all reasons set to 'unspecified'
  export function IPermissionsFromPermissions(permissions: _.Dictionary<Permission>): _.Dictionary<IPermission> {
    const permissionsWithUnspecifiedReasons: _.Dictionary<IPermission> = {};
    _.each(permissions, (permission: Permission, key: any) => {
      permissionsWithUnspecifiedReasons[key] = {
        permission: permission,
        reason: PermissionReason.Unspecified
      };
    });
    return permissionsWithUnspecifiedReasons;
  }

  // returns a new dictionary of Permissions
  export function PermissionsFromIPermissions(ipermissions: _.Dictionary<IPermission>): _.Dictionary<Permission> {
    const permissions: _.Dictionary<Permission> = {};
    _.each(ipermissions, (permission: IPermission, key: string) => {
      permissions[key] = permission.permission;
    });
    return permissions;
  }

  export function explicitPermissionsRequestsForAuthorizables(
      authorizables: ITypedIds): GetExplicitPermissionsRequest[] {
    const buildRequestFn = (authorizableIds: string[]) =>
      new GetExplicitPermissionsRequest(
        { authorizables: {type: authorizables.type, ids: authorizableIds} });
    return RequestHelper.buildRequests(authorizables.ids, buildRequestFn);
  }

  export function explicitPermissionsRequestsForAuthorizablesProjects(
      authorizables: ITypedIds): GetExplicitPermissionsProjectsRequest[] {
    const buildRequestFn = (authorizableIds: string[]) =>
      new GetExplicitPermissionsProjectsRequest(
        { authorizables: {type: authorizables.type, ids: authorizableIds} });
    return RequestHelper.buildRequests(authorizables.ids, buildRequestFn);
  }

  export function effectivePermissionsRequestsForAuthorizables(
      userIds: string[], authorizables: ITypedIds): GetEffectivePermissionsRequest[] {
    const buildRequestFn = (authorizableIds: string[]) =>
      new GetEffectivePermissionsRequest(
        { userIds: userIds, authorizables: {type: authorizables.type, ids: authorizableIds} }
    );
    return RequestHelper.buildRequests(authorizables.ids, buildRequestFn);
  }

  export function setExplicitPermissionsRequestsForGrantees(
    authorizable: ITypedId,
    grantees: ITypedId[],
    permissions: ICapabilityValue[]
  ): SetExplicitPermissionsRequest[] {

    const buildRequest = (grantees: ITypedId[]) => {
      const authorizableGranteePermissionsList: IAuthorizableGranteePermissions[] = grantees.map(grantee => {
        return { authorizable, grantee, permissions };
      });

      return new SetExplicitPermissionsRequest({ permissions: authorizableGranteePermissionsList });
    };

    // Break into multiple requests in case number of grantees exceeds block size
    return RequestHelper.buildRequests(grantees, buildRequest, RequestHelper.DefaultBlockSize);
  }

  export function setExplicitPermissionsRequestsForAuthorizables(
      authorizables: ITypedIds,
      granteePermissions: IGranteePermissions): SetExplicitPermissionsRequest[] {

    const buildRequest = (authorizableIds: string[]) => {
      const agps: IAuthorizableGranteePermissions[] = [];
      _.each(authorizableIds, (authorizableId: string) => {
        _.each(granteePermissions.permissionsByContentType,
          (permissions: _.Dictionary<IPermission>, contentType: string) => {
            // Don't create a request when no permissions have changed
            if (_.isEqual(permissions, {})) return;

            const agp: IAuthorizableGranteePermissions = {
              authorizable: { type: contentType, id: authorizableId },
              grantee: getGrantee(granteePermissions),
              permissions: capabilityValuesFromPermissions(permissions)
            };
            agps.push(agp);
          }
        );
      });

      return new SetExplicitPermissionsRequest({
        permissions: agps
      });
    };

    // Because we push multiple AGPs onto the request for each authorizable ID, we must make sure
    // that the total number of AGPs per request is still less than the maximum allowed block size (TFS 938787)
    const blockSize = Math.floor(RequestHelper.DefaultBlockSize / _.size(granteePermissions.permissionsByContentType));

    return RequestHelper.buildRequests(authorizables.ids, buildRequest, blockSize);
  }

  export function getGrantee(granteePermissions: IGranteePermissions): ITypedId {
    if (granteePermissions.group) {
      return {
        type: 'group',
        id: granteePermissions.group.id
      };
    } else if (granteePermissions.user) {
      return {
        type: 'user',
        id: granteePermissions.user.id
      };
    } else if (granteePermissions.groupSet) {
      return {
        type: 'groupAND', // At the momment groupset only supports the GroupAND operation
        id: granteePermissions.groupSet.localId.toString() // using localID for webclient API
      };
    }else {
      return null;
    }
  }

  export function capabilityValuesFromPermissions(permissions: _.Dictionary<IPermission>): ICapabilityValue[] {
    return _.map(permissions, (permission: IPermission, capability: string) => {
      const capabilityValue: ICapabilityValue = {
        capability: capability,
        value: capabilityValueFromPermission(permission.permission)
      };
      return capabilityValue;
    });
  }

  export function capabilityValueFromPermission(permission: Permission): CapabilityValue {
    switch(permission) {
      case Permission.Allowed: return CapabilityValue.Allowed;
      case Permission.Denied: return CapabilityValue.Denied;
      case Permission.Mixed: return CapabilityValue.Mixed;
      case Permission.Unspecified: return CapabilityValue.Unspecified;
      default: return null;
    }
  }

  export function hasProjectLeaderCapability(row: IGranteePermissions) {
    const contentType = 'project';
    const cap = PermissionsHelper.capabilityValue(row, contentType, 'project_leader', 'denied');
    const inheritedCap = PermissionsHelper.capabilityValue(row, contentType, 'inherited_project_leader', 'denied');
    return row == null ? false : cap === 'allowed' || inheritedCap === 'allowed';
  }

  export function addProjectLeaderPermissionsToAuthorizablePermissions(projectLeaderPermissions: ExplicitPermissionsResult, authorizablePermissions: ExplicitPermissionsResult): ExplicitPermissionsResult {
    // Dictionaries for quick lookup of grantee -> permission
    const granteeUsers = _.groupBy(_.filter(authorizablePermissions.permissionsList.items,
        (p: IGranteePermissions) => p.user != null),
      (p: IGranteePermissions) => p.user.id);
    const granteeGroups = _.groupBy(_.filter(authorizablePermissions.permissionsList.items,
        (p: IGranteePermissions) => p.group != null),
      (p: IGranteePermissions) =>  p.group.id);

    // Add users/groups with Project Leader Administer capabilities to the grantee list
    projectLeaderPermissions.permissionsList.items.forEach((granteePermissions: IGranteePermissions) => {
      let granteeProjectPermissions = granteePermissions.permissionsByContentType[AuthorizableType.PROJECT];

      if (granteeProjectPermissions[Capabilities.projectLeader] || granteeProjectPermissions[Capabilities.inheritedProjectLeader]) {
        if (granteePermissions.group && granteeGroups[granteePermissions.group.id]) {
          granteeGroups[granteePermissions.group.id][0].permissionsByContentType[AuthorizableType.PROJECT] = granteeProjectPermissions;
        } else if (granteePermissions.user && granteeUsers[granteePermissions.user.id]) {
          granteeUsers[granteePermissions.user.id][0].permissionsByContentType[AuthorizableType.PROJECT] =granteeProjectPermissions;
        } else {
          authorizablePermissions.permissionsList.items.push(granteePermissions);
        }
      }
    });

    return {
      permissionsList: new ListResult<IGranteePermissions>(authorizablePermissions.permissionsList.items),
      sourceAuthorizablesById: authorizablePermissions.sourceAuthorizablesById,
      errors: authorizablePermissions.errors
    };
  }
}
